import { useEffect, useRef, useState } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TableRow,
    TableHead,
    Paper,
    TableCell,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    TextField,
    Box,
    Alert,
    Link,
    Divider
} from '@mui/material';
import { useDispatch } from 'react-redux';
import ClearIcon from '@mui/icons-material/Clear';
import DoNotDisturbOutlinedIcon from '@mui/icons-material/DoNotDisturbOutlined';
import Paginator from '../../../../layout/Pagination';
import { v4 as uuidv4 } from 'uuid';
import { MONTHMAPPER } from '../constants/months';

const DataCapturingTable = ({
    month,
    setMonth,
    months,
    columns,
    data,
    totals,
    getDataAction,
    getTotalsAction,
    postDataAction,
    uploadCsvAction,
    TotalTableComponent,
    selectedCategory,
    categoryKey,
    fuelType,
    distributionType,
    generalInformationData,
    year,
    companyId,
    groupId,
    facilityId,
    totalRecordCount,
    rowsPerPage,
    currentPage,
    _determinePaginationMessage,
    _determinePaginationDisabledState,
    _determineRowsPerPageOptions,
    _handleChangeRowsPerPage,
    _handleChangePage
}) => {
    const dispatch = useDispatch();
    const [assetRows, setAssetRows] = useState([]);
    const [formErrors, setFormErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null)
    const [currentCsvDocument, setCurrentCsvDocument] = useState(null)
    const [csvLoading, setCsvLoading] = useState(false)

    // When the category changes, force a re-render and change the months to 0 to prevent the total bug
    useEffect(() => {
        setMonth(0)
        setCurrentCsvDocument(null)
    }, [categoryKey, distributionType])

    useEffect(() => {
        setFormErrors(null);
        setAssetRows(data)
    }, [data, dispatch, month, months]);

    const handleMonthChange = (event, newValue) => {
        setMonth(newValue);
        const monthForBackend = MONTHMAPPER[newValue];
        // 12 is the total tab
        if (newValue === 12) {
            if (fuelType) {
                dispatch(getTotalsAction(categoryKey, year, facilityId));
            } else {
                dispatch(getTotalsAction(year, facilityId, distributionType));
            }
        } else {
            if (fuelType) {
                dispatch(getDataAction(categoryKey, year, facilityId, rowsPerPage, currentPage, monthForBackend));
            }
            else if (distributionType) {
                dispatch(getDataAction(year, facilityId, distributionType, rowsPerPage, currentPage, monthForBackend));
            }
            else {
                dispatch(getDataAction(year, facilityId, rowsPerPage, currentPage, monthForBackend));
            }
        }
    };

    const handleAddAsset = async () => {

        setAssetRows(prevRows => {
            return prevRows = [
                ...prevRows,
                { nonPersistent: true, rowId: uuidv4(), ...Object.fromEntries(columns.map(col => [col.field, ''])) }
            ];
        });

    };

    function changeSingleUnitFields(rowToEdit, value) {
        if (value === 'Lactic Acid') {
            rowToEdit['unit'] = 'Litres';
        }
        if (value === 'Yeast' || value === 'Sulphur Dioxide' || value === 'Carbon Dioxide' || value === 'Bulk Grapes'
        ) {
            rowToEdit['unit'] = 'Kilograms';
        }
        if (value === 'Bottled Wine' || value === 'Bulk Wine') {
            rowToEdit['unit'] = 'Kilolitres';
        }
    }

    // Dynamic handlers for each field type
    const handleFieldChange = (field, value, id) => {
        setAssetRows((prevState) =>
            prevState.map((item) => {
                if ((item['id'] === id) || item['rowId'] === id) {
                    const updatedItem = { ...item, [field]: value, dirty: true };
                    if (
                        field === 'vehicleRegistration' &&
                        generalInformationData &&
                        generalInformationData.length > 0
                    ) {
                        const selectedPetrolCardHolder = generalInformationData.find(
                            petrolCardHolder => petrolCardHolder.vehicleRegistration === value
                        );
                        if (selectedPetrolCardHolder) {
                            updatedItem['cardHolder'] = selectedPetrolCardHolder.cardHolder;
                            updatedItem['petrolCardHolderId'] = selectedPetrolCardHolder.id;
                        }
                    }
                    changeSingleUnitFields(updatedItem, value, categoryKey);
                    return updatedItem;
                }
                return item;
            })
        );
    };

    const handleFileUpload = (e, id) => {
        handleFieldChange('file', e.target.files[0], id);
    };

    const handleSubmit = async () => {
        setFormErrors(null);
        setIsLoading(true);
        let errors = '';

        // Loop through all rows in assetRows array
        assetRows.forEach(item => {
            // Check all columns except optional ones (e.g., file)
            columns.forEach(col => {
                if (col.type !== 'file' && (!item[col.field] || item[col.field] === '')) {
                    errors = 'Please complete all required form fields';
                }
            });
        });

        if (errors) {
            setFormErrors(errors);
            setIsLoading(false)
            return;
        }

        let formData = new FormData();
        const filteredDirtyRows = assetRows.filter(item => item.dirty === true);

        formData.append('data', JSON.stringify(filteredDirtyRows));

        filteredDirtyRows.forEach((item, index) => {
            if (item.file instanceof File) {
                formData.append(`file_${index}`, item.file);
            }
        });

        const monthForBackend = MONTHMAPPER[month];

        try {
            if (fuelType) {
                await dispatch(postDataAction(formData, categoryKey, year, companyId, groupId, facilityId, monthForBackend))
                await dispatch(getDataAction(categoryKey, year, facilityId, rowsPerPage, currentPage, monthForBackend))

            }
            else if (distributionType) {
                await dispatch(postDataAction(formData, year, companyId, groupId, facilityId, distributionType, monthForBackend))
                await dispatch(getDataAction(year, facilityId, distributionType, rowsPerPage, currentPage, monthForBackend))
            }
            else {
                await dispatch(postDataAction(formData, year, companyId, groupId, facilityId, monthForBackend))
                await dispatch(getDataAction(year, facilityId, rowsPerPage, currentPage, monthForBackend))

            }
        } catch (error) {
            setFormErrors('An error occurred while submitting the form');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveLastRow = (rowId) => {
        setAssetRows(prevRows => {
            const updatedMonthRows = prevRows.filter(row => row.rowId !== rowId);
            return [...updatedMonthRows];
        });
    };

    const handleChangeCsvFile = (event) => {
        const { type } = event.target
        const file = event.target.files[0]
        if (type === 'file' && file) {
            if (file.name.toLowerCase().endsWith('.csv')) {
                setCurrentCsvDocument(event.target.files[0])
                setFormErrors(null)
            }
            else {
                setFormErrors('Please upload a CSV file (.csv extension)')
                handleResetCsv()
            }
        }
        else {
            setFormErrors('Please ensure your upload target is a file.')
            handleResetCsv()
        }
    }

    const uploadCsv = async () => {
        try {
            setCsvLoading(true)
            const formData = new FormData()
            formData.append('file', currentCsvDocument)
            await dispatch(uploadCsvAction(formData, companyId, year, groupId, facilityId, selectedCategory))
            // workaround for now to force re-fetch
            setMonth(0)
            const monthForBackend = MONTHMAPPER[0];
            // 12 is the total tab
            if (month === 12) {
                if (fuelType) {
                    dispatch(getTotalsAction(categoryKey, year, facilityId));
                } else {
                    dispatch(getTotalsAction(year, facilityId, distributionType));
                }
            } else {
                if (fuelType) {
                    dispatch(getDataAction(categoryKey, year, facilityId, rowsPerPage, currentPage, monthForBackend));
                }
                else if (distributionType) {
                    dispatch(getDataAction(year, facilityId, distributionType, rowsPerPage, currentPage, monthForBackend));
                }
                else {
                    dispatch(getDataAction(year, facilityId, rowsPerPage, currentPage, monthForBackend));
                }
            }
            handleResetCsv()
            setFormErrors(null)
        } catch (error) {
            setFormErrors('Could not upload CSV. Please try again.')
            handleResetCsv()
        } finally { setCsvLoading(false) }
    }

    const handleResetCsv = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        setCurrentCsvDocument(null)
    }

    const hasEntriesWithoutId = assetRows?.some(item => !item.id);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 3 }}
            >
                <Button
                    variant={currentCsvDocument ? 'outlined' : 'contained'}
                    component="label"
                    sx={{ width: 'fit-content' }}
                // disabled={!selectedCategory.toLowerCase().includes('distribution')}
                >
                    {currentCsvDocument
                        ? `Queued File: ${currentCsvDocument.name}`
                        : 'Select CSV File to Upload'
                    }
                    <input
                        type="file"
                        hidden
                        name="file"
                        ref={fileInputRef}
                        onChange={handleChangeCsvFile}
                        accept=".csv,text/csv"
                    />
                </Button>
                <Button
                    variant='contained'
                    sx={{ width: 'fit-content' }}
                    onClick={() => uploadCsv()}
                    disabled={csvLoading
                        // || !selectedCategory.toLowerCase().includes('distribution')
                        || currentCsvDocument === null}
                >
                    {csvLoading
                        ? 'Uploading CSV'
                        : `Upload ${selectedCategory} CSV Document`}
                </Button>
            </Box>
            <Tabs
                value={month}
                onChange={handleMonthChange}
                sx={{ width: '100%', float: 'left' }}
                textColor='inherit'
                indicatorColor='secondary'
            >
                {months.map((m, i) => {
                    return <Tab key={i} label={m['month']} sx={{ minWidth: '25px' }} />
                }
                )}
            </Tabs>
            {month === 12 && TotalTableComponent
                ? <TotalTableComponent dataTotals={totals} />
                : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}> {
                        assetRows?.length === 0
                            ? <NoAssetRows category={selectedCategory} month={month} year={year} />
                            : <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <TableContainer component={Paper} sx={{ height: 'auto' }}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            <TableRow>
                                                {columns.map(col => (
                                                    <TableCell key={col.field} align="left">
                                                        {col.label}
                                                    </TableCell>
                                                ))}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        minWidth: '10%',
                                                        maxWidth: '10%',
                                                    }}
                                                >
                                                    Remove Row
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {assetRows?.map((row, index) => (
                                                <TableRow key={index}>
                                                    {columns.map(column => {
                                                        if (column.type === 'select') {
                                                            return (
                                                                <TableCell key={column.field} align="left">
                                                                    <FormControl sx={{ width: '200px' }}>
                                                                        <InputLabel id={column.field}>{column.selectLabel}</InputLabel>
                                                                        <Select
                                                                            required
                                                                            name={column.field}
                                                                            disabled={column.disabled}
                                                                            onChange={(e) => {
                                                                                if (row.rowId) {
                                                                                    handleFieldChange(column.field, e.target.value, row.rowId);
                                                                                } else {
                                                                                    handleFieldChange(column.field, e.target.value, row.id)
                                                                                }
                                                                            }}
                                                                            value={row[column.field] || ''}
                                                                            label={column.selectLabel}
                                                                        >
                                                                            {column?.options?.map(item => (
                                                                                <MenuItem
                                                                                    key={item.id}
                                                                                    data-id={item.id}
                                                                                    value={item.value}
                                                                                >
                                                                                    {item.value}
                                                                                </MenuItem>
                                                                            ))
                                                                            }
                                                                        </Select>
                                                                    </FormControl>
                                                                </TableCell>
                                                            );
                                                        }
                                                        if (column.type === 'text') {
                                                            return (
                                                                <TableCell key={column.field} align="left">
                                                                    <TextField
                                                                        required
                                                                        fullWidth
                                                                        name={column.field}
                                                                        value={row[column.field] || ''}
                                                                        onChange={(e) => {
                                                                            if (row.rowId) {
                                                                                handleFieldChange(column.field, e.target.value, row.rowId);
                                                                            } else {
                                                                                handleFieldChange(column.field, e.target.value, row.id)
                                                                            }
                                                                        }}
                                                                        type="text"
                                                                        placeholder={column.placeholder || ''}
                                                                        disabled={column.disabled}
                                                                        InputProps={{
                                                                            sx: {
                                                                                '& input::placeholder': {
                                                                                    fontStyle: 'italic',
                                                                                },
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        }
                                                        if (column.type === 'file') {
                                                            return (
                                                                <TableCell
                                                                    key={column.field}
                                                                    align="left">
                                                                    {row?.file ? (
                                                                        <>
                                                                            <Link
                                                                                href={row.file}
                                                                                target='_blank'
                                                                                name={column.field}
                                                                            >
                                                                                {row.file.name ? row.file.name : row.file ? 'Uploaded Document' : ''}
                                                                            </Link>
                                                                            <input
                                                                                style={{ display: 'none' }}
                                                                                name={column.field}
                                                                                onChange={(e) => {
                                                                                    if (row.rowId) {
                                                                                        handleFileUpload(e, row.rowId);
                                                                                    } else {
                                                                                        handleFileUpload(e, row.id)
                                                                                    }
                                                                                }}
                                                                                type="file"
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                            <input
                                                                                id={`file-input-${row.rowId || row.id}`}
                                                                                style={{ display: 'none' }}
                                                                                name={column.field}
                                                                                onChange={(e) => {
                                                                                    if (row.rowId) {
                                                                                        handleFileUpload(e, row.rowId);
                                                                                    } else {
                                                                                        handleFileUpload(e, row.id)
                                                                                    }
                                                                                }}
                                                                                type="file"
                                                                            />
                                                                            <label htmlFor={`file-input-${row.rowId || row.id}`}>
                                                                                <Button variant="contained" component="span" size="small">
                                                                                    Upload File
                                                                                </Button>
                                                                            </label>
                                                                        </Box>
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        }
                                                        return <TableCell key={column.field} />;
                                                    })}
                                                    <TableCell align="left">
                                                        {assetRows.length > 0 && hasEntriesWithoutId && row.nonPersistent
                                                            ? (<Box
                                                                onClick={() => handleRemoveLastRow(row.rowId)}
                                                                sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', cursor: 'pointer', color: 'red' }}>
                                                                <ClearIcon />
                                                            </Box>
                                                            )
                                                            : <Box
                                                                sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                                                <DoNotDisturbOutlinedIcon />
                                                            </Box>
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                    }
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paginator
                                totalRecordCount={totalRecordCount}
                                rowsPerPage={rowsPerPage}
                                currentPage={currentPage}
                                handleChangePage={_handleChangePage}
                                handleChangeRowsPerPage={_handleChangeRowsPerPage}
                                determinePaginationMessage={_determinePaginationMessage}
                                determinePaginationDisabledState={_determinePaginationDisabledState}
                                determineRowsPerPageOptions={_determineRowsPerPageOptions}
                            />
                            {formErrors && (
                                <Alert severity='error' sx={{ width: 'fit-content' }}>{formErrors}</Alert>
                            )}
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Button variant="contained" onClick={handleAddAsset}>
                                    Add Entry
                                </Button>
                                <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
                                    {isLoading ? 'Submitting...' : 'Submit Data'}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
        </Box>
    );
};

export default DataCapturingTable;

function NoAssetRows({ category, month, year }) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignContent: 'center',
            width: '100%',
            height: 160,
            borderRadius: '3px',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)',
        }}>
            <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignContent: 'center',
                gap: 2
            }}>
                <Divider sx={{ pb: 1 }} />
                <Alert severity='info'
                    sx={{
                        width: 'fit-content',
                        justifySelf: 'center',
                        alignSelf: 'center',
                    }}>No {category} data available for {MONTHMAPPER[month]} {year}.</Alert>
            </Box>
        </Box>
    )
}